import { createFileRoute } from "@tanstack/react-router";
import { useLanguage } from "@/i18n/LanguageProvider";
import { Calendar, MapPin, Clock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/events")({
  head: () => ({ meta: [{ title: "Events — Code Cubs Academy" }] }),
  component: EventsPage,
});

function EventsPage() {
  const { t } = useLanguage();
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchEvents() {
      const { data } = await supabase
        .from("events")
        .select("*")
        .eq("is_published", true)
        .order("event_date", { ascending: true });

      setEvents(data || []);
      setLoading(false);
    }
    fetchEvents();
  }, []);

  return (
    <div className="bg-background text-foreground min-h-screen">
      <section className="container mx-auto px-4 py-16 text-center">
        <h1 className="text-4xl lg:text-6xl font-extrabold mb-4">{t.home.eventsTitle}</h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          اكتشف أحدث الفعاليات والأنشطة التقنية التي ننظمها لأطفالنا المبدعين.
        </p>
      </section>

      <section className="container mx-auto px-4 pb-24">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            لا توجد فعاليات قادمة حالياً.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {events.map((event) => (
              <div
                key={event.id}
                className="bg-surface border border-surface-elevated rounded-3xl overflow-hidden hover:border-primary/50 transition-all group"
              >
                <div className="h-48 overflow-hidden relative">
                  <img
                    src={
                      event.image_url ||
                      "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&q=80&w=800"
                    }
                    alt={event.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 start-4 bg-background/90 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-bold text-primary flex items-center gap-1.5 shadow-sm">
                    <Calendar className="w-3.5 h-3.5" />
                    {new Date(event.event_date).toLocaleDateString("ar-EG")}
                  </div>
                </div>
                <div className="p-6 space-y-4 flex flex-col h-full">
                  <h2 className="text-xl font-bold leading-snug">{event.title}</h2>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-primary/70" />
                      <span>
                        {new Date(event.event_date).toLocaleTimeString("ar-EG", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-primary/70" />
                      <span>{event.location || "أونلاين"}</span>
                    </div>
                  </div>
                  <p className="text-sm leading-relaxed text-foreground/80 line-clamp-3 mb-4 flex-1">
                    {event.description}
                  </p>
                  <div className="pt-4 border-t border-surface-elevated mt-auto">
                    {event.registration_url ? (
                      <Button
                        asChild
                        className="w-full rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground font-bold"
                      >
                        <a href={event.registration_url} target="_blank" rel="noopener noreferrer">
                          حجز مقعد
                        </a>
                      </Button>
                    ) : (
                      <Button disabled className="w-full rounded-xl">
                        الحجز مغلق
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
